import { create } from 'zustand'
import type { SoundName } from './clips'

/** Per device: a bar tablet and a personal phone usually want different sounds. */
export interface SoundSettings {
  enabled: boolean
  /** 0..1 */
  volume: number
  vibrate: boolean
  events: Record<SoundName, boolean>
}

export const DEFAULT_SOUND_SETTINGS: SoundSettings = {
  enabled: true,
  volume: 0.7,
  vibrate: true,
  events: { message: true, direct: true, mention: true, sent: true, deviceRequest: true },
}

const STORAGE_KEY = 'boss-chat.sounds.v1'

export function loadSoundSettings(storage: Pick<Storage, 'getItem'> | undefined = safeStorage()): SoundSettings {
  try {
    const raw = storage?.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_SOUND_SETTINGS
    const parsed = JSON.parse(raw) as Partial<SoundSettings>
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULT_SOUND_SETTINGS.enabled,
      volume: typeof parsed.volume === 'number' && parsed.volume >= 0 && parsed.volume <= 1 ? parsed.volume : DEFAULT_SOUND_SETTINGS.volume,
      vibrate: typeof parsed.vibrate === 'boolean' ? parsed.vibrate : DEFAULT_SOUND_SETTINGS.vibrate,
      events: { ...DEFAULT_SOUND_SETTINGS.events, ...(parsed.events ?? {}) },
    }
  } catch {
    return DEFAULT_SOUND_SETTINGS
  }
}

function saveSoundSettings(settings: SoundSettings): void {
  try {
    safeStorage()?.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Storage can be unavailable (private mode, quota); settings then last for this page session.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

interface SoundSettingsState extends SoundSettings {
  update: (patch: Partial<Omit<SoundSettings, 'events'>> & { events?: Partial<Record<SoundName, boolean>> }) => void
}

export const useSoundSettings = create<SoundSettingsState>((set, get) => ({
  ...loadSoundSettings(),
  update: (patch) => {
    const current = get()
    const next: SoundSettings = {
      enabled: patch.enabled ?? current.enabled,
      volume: patch.volume ?? current.volume,
      vibrate: patch.vibrate ?? current.vibrate,
      events: { ...current.events, ...(patch.events ?? {}) },
    }
    saveSoundSettings(next)
    set(next)
  },
}))
